import {HttpStatusCode} from 'axios';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getPermissionsbyProjectId} from '../../api/ClientManagementService';
import {PERMISSION_TYPE} from '../../constants/constants';
import {setPermissionsByProject} from '../../redux/slices/projectPermissionsSlice';
import {RootState} from '../../redux/store';
import {isEmptyInstance} from '../../utils/utils';

const usePermissionsByProjectId = (projectId?: string) => {
  const dispatch = useDispatch();
  const [isPermissionLoading, setIsPermissionLoading] =
    useState<boolean>(false);
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const permissionsByProject = useSelector(
    (state: RootState) => state.projectPermissions.permissionsByProject
  );

  const permissions = useMemo(() => {
    return permissionsByProject?.[projectId ?? ''];
  }, [permissionsByProject, projectId]);

  const getPermissionsByClientIdProjectId = useCallback(
    async (id?: string) => {
      const projectID = projectId ?? id;

      if (!isEmptyInstance(clientId) && !isEmptyInstance(projectID)) {
        try {
          setIsPermissionLoading(true);
          const response = await getPermissionsbyProjectId(clientId, projectID);
          setPermissions(response, projectID);
        } catch (error) {
          console.error(error);
        } finally {
          setIsPermissionLoading(false);
        }
      }
    },
    [clientId, dispatch, projectId]
  );

  const setPermissions = (response: any, projectID: string) => {
    if (response?.status === HttpStatusCode.Ok) {
      const defaultPerms = response?.data?.data?.value?.permissions ?? [];
      const userRole = response?.data?.data?.value?.userRole?.find(
        (role: any) => role.client?.clientId == clientId
      );

      const projectPermissionGroup =
        userRole?.projectWorkspaceList?.find(
          (proj: any) => proj.id == projectID
        )?.projectPermissions ?? [];

      const mergeByType = (type: string, useProjectPrecedence = false) => {
        const defaultByType = _.filter(defaultPerms, (p) => p?.type === type);
        const projectByType = _.filter(
          projectPermissionGroup,
          (p) => p?.type === type
        );

        if (useProjectPrecedence && projectByType.length > 0) {
          return projectByType;
        }

        return _.uniqBy(_.concat(defaultByType, projectByType), 'permissionId');
      };

      const systemPermissions = mergeByType(PERMISSION_TYPE.SYSTEM);
      const manageProjectPermissions = mergeByType(
        PERMISSION_TYPE.MANAGE_PROJECT,
        true
      );
      const reportPermissions = mergeByType(PERMISSION_TYPE.REPORTS, true);
      const budgetPermissions = mergeByType(PERMISSION_TYPE.BUDGET);
      const eciPermissions = mergeByType(PERMISSION_TYPE.ECI);
      const evidenceSourcePermissions = mergeByType(PERMISSION_TYPE.EVIDENCE);

      const otherPermissions = _.filter(
        _.uniqBy(
          _.concat(defaultPerms, projectPermissionGroup),
          'permissionId'
        ),
        (permission) =>
          ![
            PERMISSION_TYPE.SYSTEM,
            PERMISSION_TYPE.MANAGE_PROJECT,
            PERMISSION_TYPE.REPORTS,
            PERMISSION_TYPE.BUDGET,
            PERMISSION_TYPE.ECI,
            PERMISSION_TYPE.EVIDENCE,
          ].includes(permission?.type)
      );

      const allPermissions = _.uniqBy(
        _.concat(
          systemPermissions,
          manageProjectPermissions,
          reportPermissions,
          budgetPermissions,
          eciPermissions,
          evidenceSourcePermissions,
          otherPermissions
        ),
        'permissionId'
      );

      dispatch(
        setPermissionsByProject({
          projectId: projectID,
          permissions: allPermissions,
        })
      );
    }
  };

  useEffect(() => {
    if (permissions !== undefined) return;

    getPermissionsByClientIdProjectId();
  }, [getPermissionsByClientIdProjectId, permissions]);

  return {
    isPermissionLoading,
    getPermissionsByClientIdProjectId,
    setPermissions,
  };
};

export default usePermissionsByProjectId;
